import {Grid, Typography} from "@material-ui/core";
import {useEffect, useMemo, useState} from "react";
import {
    AutocompleteArrayInput,
    AutocompleteInput,
    Button, DateTimeInput,
    NullableBooleanInput,
    ReferenceArrayInput,
    ReferenceInput,
    SelectArrayInput,
    useGetOne,
} from "react-admin";
import {calculateEtaDuration, getPointsGroupedDeliveries} from "./utils";
import {StatsCard} from "./components/StatsCard";
import {useStore} from "./store";
import {Delivery, DeliveryType, Subject} from "../shared/types";
import {ReferenceInputSubject} from "../components/reference-inputs/ReferenceInputSubject";
import "leaflet-area-select";
import {useOptions} from "../shared/hooks/useOptions";
import {initField} from "../shared/utils";
import {CustomList} from "../components/CustomList";
import {RoutePlannerMap} from "./components/RoutePlannerMap";
import {toInteger} from "lodash";
import {getUser} from "../authProvider";
import {client} from "../dataProvider";
import {EtaParams} from "./store";

export const RoutePlannerPage = () => {
    const {data: optionsDelivery} = useOptions("deliveries", "GET");
    const {data: optionsSubjectOpenings} = useOptions("subjectopenings", "GET");


    const filters = optionsDelivery && optionsSubjectOpenings
        ? [
            <ReferenceArrayInput
                helperText={false}
                fullWidth
                source="province"
                reference="autocomplete/province"
                label="Provincia"
            >
                <AutocompleteArrayInput optionText="label" resettable/>
            </ReferenceArrayInput>,
            <ReferenceInputSubject
                allowEmpty
                fullWidth
                source="store"
                filter={{type: "default_store"}}
                label="Magazzino"
                helperText={false}
            />,
            <ReferenceArrayInput
                helperText={false}
                fullWidth
                source="receivers"
                reference="autocomplete/subject"
                label="Destinatari"
            >
                <AutocompleteArrayInput optionText="label" resettable/>
            </ReferenceArrayInput>,
            <ReferenceArrayInput
                helperText={false}
                fullWidth
                source="clients"
                filter={{type: "client"}}
                reference="autocomplete/subject"
                label="Clienti"
            >
                <AutocompleteArrayInput optionText="label" resettable/>
            </ReferenceArrayInput>,
            <ReferenceInput
                helperText={false}
                fullWidth
                source="imported_file"
                reference="autocomplete/imported-files"
                label="File"
            >
                <AutocompleteInput optionText="label" resettable/>
            </ReferenceInput>,
            <ReferenceArrayInput
                source="region"
                label="Regioni"
                reference="autocomplete/region"
                className="filter-field-small"
            >
                <AutocompleteArrayInput optionText="label"/>
            </ReferenceArrayInput>,
            <ReferenceArrayInput
                helperText={false}
                fullWidth
                source="zones"
                filter={{type: "zones"}}
                reference="autocomplete/delivery-zones"
                label="Zone"
            >
                <AutocompleteArrayInput optionText="label" resettable/>
            </ReferenceArrayInput>,
            <NullableBooleanInput
                fullWidth
                source="to_be_tractioned"
                label="Da trazionare"
                alwaysOn
            />,
            <NullableBooleanInput
                fullWidth
                source="is_recurrent_child"
                label="Generata da ricorrente"
            />,
            <DateTimeInput
                fullWidth
                source="delivery_date_before"
                label="Data missione fino al"
            />,
            <SelectArrayInput
                {...initField("delivery_types", optionsDelivery, "array")}
                fullWidth
                allowEmpty
                choices={optionsDelivery.delivery_type.choices}
                label="Tipi missioni"
            />,
            <NullableBooleanInput
                source="note"
                label="Contiene bolle"
            />,
            <ReferenceArrayInput
                reference="autocomplete/category"
                defaultValue={[]}
                source={"categories"}
                label="Categorie"
                sort={{"field": "label", "order": "ASC"}}
                className="filter-field-small">
                <AutocompleteArrayInput optionText="label" fullWidth/>
            </ReferenceArrayInput>,
            <SelectArrayInput
                {...initField("openings", optionsDelivery, "array")}
                fullWidth
                allowEmpty
                choices={optionsSubjectOpenings.day.choices}
                label="Giorni apertura"
                alwaysOn
            />,
        ]
        : [];
    const {
        addedSegments,
        vehicle,
        groupedDeliveries,
        finalPointId,
        startPointId,
        startDate,
        warehouseAlert,
        updateStartDate,
        updateStartPointId,
        updateFinalPointId,
        setEtaParams,
        etaParams
    } = useStore();
    const [totKm, setTotKm] = useState(0);
    const [totDuration, setTotDuration] = useState(0);
    const [pointsDurationMapping, setPointsDurationMapping] = useState<any[]>([]);
    const [etaParamsLoaded, setEtaParamsLoaded] = useState(false);
    const [etaAuditEnabled, setEtaAuditEnabled] = useState(false);
    const [routingReady, setRoutingReady] = useState(false);

    useEffect(() => {
        client("configs/eta", {method: "GET"})
            .then((config: { params: EtaParams, audit_enabled?: boolean }) => {
                setEtaParams(config.params);
                setEtaAuditEnabled(config.audit_enabled === true);
                setEtaParamsLoaded(true);
            })
            .catch(() => {
                // Mantiene i valori di default dello store se la configurazione non è disponibile.
            });
    }, [setEtaParams]);

    const count_list_destination = groupedDeliveries.reduce(
        (seconds, deliveryGroup) => {
            if (
                (!deliveryGroup.isPickUpPoint && deliveryGroup.delivery_type !== DeliveryType.WITHDRAW) ||
                (deliveryGroup.isPickUpPoint && deliveryGroup.delivery_type === DeliveryType.WITHDRAW)
            ) seconds += 1;
            return seconds;
        },
        0
    )

    const mainStats = useMemo(() => {
        return addedSegments
            .reduce((deliveries, segment) => {
                if (
                    !deliveries.find((delivery) => delivery.id === segment.delivery.id)
                ) {
                    deliveries.push(segment.delivery);
                }
                return deliveries;
            }, [] as Delivery[])
            .reduce(
                (previous, current) => {
                    return {
                        leftWeight: previous.leftWeight - current.weight!,
                        maxWeight: previous.maxWeight,
                        currentWeight: previous.currentWeight + (current.delivery_type === DeliveryType.WITHDRAW ? 0 : current.weight!),
                        showWeightAlert: (
                            ((previous.leftWeight - current.weight! < 0) && previous.maxWeight !== 0) ||
                            (previous.maxPackages - (previous.totalPackages + current.packages_number!) < 0 && previous.maxPackages !== 0)
                        )
                            ? true : false,
                        totalPackages: previous.totalPackages + current.packages_number!,
                        maxPackages: previous.maxPackages,
                        currentPackages:
                            previous.currentPackages + (current.delivery_type === DeliveryType.WITHDRAW ? 0 : current.packages_number!),
                        n_destination: count_list_destination
                    };
                },
                {
                    leftWeight: vehicle ? vehicle.max_weigth : 0,
                    maxWeight: vehicle ? vehicle.max_weigth : 0,
                    currentWeight: 0,
                    showWeightAlert: false,
                    totalPackages: 0,
                    maxPackages: vehicle ? vehicle.max_items : 0,
                    currentPackages: 0,
                    n_destination: 0,
                }
            );
    }, [addedSegments, vehicle]);

    const {data: finalPointSubject} = useGetOne("subjects", finalPointId!, {
        enabled: !!finalPointId,
    });
    const {data: startPointSubject} = useGetOne("subjects", startPointId!, {
        enabled: !!startPointId,
    });
    const points = useMemo(() => {
        const points = getPointsGroupedDeliveries(
            groupedDeliveries,
            startPointSubject as Subject,
            finalPointSubject as Subject
        );
        return points;
    }, [groupedDeliveries, finalPointSubject, startPointSubject]);
    const etaDuration = useMemo(() => {
        const totals = addedSegments.reduce((current, segment) => ({
            weight: current.weight + (segment.delivery.weight || 0),
            packages: current.packages + (segment.delivery.packages_number || 0),
            volume: current.volume + (segment.delivery.volume || 0),
            tot_cachet: current.tot_cachet + (segment.delivery?.cachet ? 1 : 0)
        }), {weight: 0, packages: 0, volume: 0, tot_cachet: 0});

        return calculateEtaDuration(
            totDuration,
            addedSegments.length,
            count_list_destination,
            totals.weight,
            totals.packages,
            totals.volume,
            totals.tot_cachet,
            etaParams
        );
    }, [addedSegments, count_list_destination, etaParams, totDuration]);

    const endDate = useMemo(() => {
        const newDate = new Date(startDate);
        newDate.setSeconds(
            newDate.getSeconds() + etaDuration
        );
        return newDate;
    }, [etaDuration, startDate]);
    const duration = useMemo(() => {
        let minutes = etaDuration / 60.0
        let zero_h = toInteger(minutes / 60) >= 10 ? "" : "0"
        let zero_m = toInteger(minutes % 60) >= 10 ? "" : "0"
        return `${zero_h}${toInteger(minutes / 60)}:${zero_m}${toInteger(minutes % 60)}`; // HOUR: MINUTES

    }, [etaDuration]);

    useEffect(() => {
        setRoutingReady(false);
        setPointsDurationMapping([]);
        if (points.length <= 0) {
            setTotKm(0);
            setTotDuration(0);
        }
    }, [points]);

    const onRoutePointsFound = (totalDistance: number, totalDuration: number, pointsDurationMapping: any[]) => {
        setTotKm(totalDistance);
        setTotDuration(totalDuration);
        setPointsDurationMapping(pointsDurationMapping)
        setRoutingReady(true);
    };
    const user = getUser();
    return (
        <CustomList
            filters={filters}
            filter={{map_filter: true}}
            resource="segments"
            basePath="/planner"
            syncWithLocation={true}
            filterDefaultValues={{to_be_tractioned: false, route_id: []}}
            pagination={<></>}
            title={
                <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Grid item xs={12}>
                        <StatsCard {...mainStats} totalKm={totKm}/>
                    </Grid>
                </Grid>
            }
            shouldExport={false}
        >
            <RoutePlannerMap
                mainStats={mainStats}
                points={points}
                filters={filters}
                onRoutePointsFound={onRoutePointsFound}
                totKm={totKm}
                duration={duration}
                etaDuration={etaDuration}
                etaParams={etaParams}
                etaParamsLoaded={etaParamsLoaded}
                etaAuditEnabled={etaAuditEnabled}
                routingDuration={totDuration}
                routingReady={routingReady}
                pointsDurationMapping={pointsDurationMapping}
                endDate={endDate ? endDate.toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'}) : ""}
                endDateValue={endDate}
            />
        </CustomList>
    );
};
